import React, { useEffect, useRef } from 'react';
import { DealerCard } from './DealerCard';
import { useDealerSearchWithQuery } from '../../hooks/userDealerSearchWithQuery';
import { Alert, QueryErrorAlert, SearchBar, LoadingSpinner } from '@shared/components';
import pluralise from '@/app/utils/formatPluralise';
import type { Dealer } from '../../types/dealers.types';

interface DealerSearchProps {
    onSelectDealer: (dealer: Dealer | null) => void;
    selectedDealer?: Dealer | null; // Optional prop to allow external selection
    /** Restricts the dealers query to dealers the caller can sell this product for. */
    sellingProductId?: string;
    /**
     * Replaces the default "No Dealers Found" block when empty AND no APPLIED
     * (debounced) search term is active; a search-caused empty result keeps the
     * default "No dealers match" block.
     */
    emptyState?: React.ReactNode;
    /** Lets the embedding page retry its own dealer reads alongside the list. */
    onRetry?: () => void;
    /** Reports whether a successful, unsearched request returned no dealers. */
    onUnsearchedEmptyChange?: (isEmpty: boolean) => void;
}

export const DealerSearch: React.FC<DealerSearchProps> = ({
    onSelectDealer,
    selectedDealer: passedInDealer,
    sellingProductId,
    emptyState,
    onRetry,
    onUnsearchedEmptyChange,
}) => {
    const {
        dealers,
        searchTerm,
        appliedSearchTerm,
        setSearchTerm,
        selectedDealer,
        selectDealer,
        clearSelection,
        resetSearch,
        isLoading,
        isFetching,
        error,
        refetch,
        totalDealers,
        filteredCount,
    } = useDealerSearchWithQuery({ sellingProductId });

    // Persisted quote selection (prop) wins; fall back to local state for the
    // standalone /dealers/search embedding where no prop is provided.
    const effectiveSelectedDealer = passedInDealer ?? selectedDealer;

    const retryPendingRef = useRef(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const queryErrorRef = useRef<HTMLDivElement>(null);

    // Retry unmounts while the request is in flight, so focus would fall to the body.
    useEffect(() => {
        if (!retryPendingRef.current || isLoading) return;
        retryPendingRef.current = false;
        const active = document.activeElement;
        if (active && active !== document.body) return;
        (error ? queryErrorRef.current?.querySelector('button') : searchInputRef.current)?.focus();
    }, [error, isLoading]);

    const isUnsearchedEmpty = !error && !isLoading && !appliedSearchTerm && dealers.length === 0;

    useEffect(() => {
        onUnsearchedEmptyChange?.(isUnsearchedEmpty);
    }, [isUnsearchedEmpty, onUnsearchedEmptyChange]);

    const handleRetry = () => {
        retryPendingRef.current = true;
        void refetch();
        onRetry?.();
    };

    const handleSelectDealer = (dealer: Dealer) => {
        selectDealer(dealer);
        onSelectDealer(dealer);
    };

    const handleClearSelection = () => {
        clearSelection();
        onSelectDealer(null);
    };

    if (error) {
        return (
            <div ref={queryErrorRef} className="mt-4 space-y-6">
                <QueryErrorAlert message="Dealers could not be loaded." onRetry={handleRetry} isRetrying={isFetching} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Search Input */}
            <div className="max-w-md">
                <SearchBar
                    ref={searchInputRef}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    placeholder="Search by name, address, or postcode..."
                />
            </div>

            {/* Selected Dealer Display */}
            {!passedInDealer && selectedDealer && (
                <Alert variant="success" role="status">
                    <div className="grow">
                        <h3 className="font-semibold">Selected Dealer</h3>
                        <p>{selectedDealer.name}</p>
                    </div>
                    <button onClick={handleClearSelection}>Clear Selection</button>
                </Alert>
            )}

            {/* Results Count */}
            {searchTerm && !isLoading && (
                <div className="text-sm text-mp-muted">
                    Showing {filteredCount} of {pluralise('dealer', totalDealers, true)}
                </div>
            )}

            {/* Dealer Grid */}
            {isLoading ? (
                <LoadingSpinner message="Loading authorised dealers" className="p-4" />
            ) : dealers.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3 lg:gap-5">
                    {dealers.map((dealer) => (
                        <DealerCard
                            key={dealer.id}
                            dealer={dealer}
                            className="w-full"
                            onSelectDealer={handleSelectDealer}
                            isSelected={effectiveSelectedDealer?.id === dealer.id}
                        />
                    ))}
                </div>
            ) : emptyState && !appliedSearchTerm ? (
                <>{emptyState}</>
            ) : (
                <Alert variant="warning" role="status">
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-semibold">No Dealers Found</p>
                        <p>
                            {appliedSearchTerm
                                ? `No dealers match "${appliedSearchTerm}"`
                                : 'No dealers available at the moment'}
                        </p>
                        {appliedSearchTerm && (
                            <button onClick={resetSearch} className="underline">
                                Clear search and show all dealers
                            </button>
                        )}
                    </div>
                </Alert>
            )}
        </div>
    );
};
